import { User, Users as UsersIcon, Shield, Building2, Plus, Edit2, Trash2, X } from 'lucide-react';
import { User as UserType } from '../App';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface SettingsPageProps {
  currentUser: UserType;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  isAdmin: boolean;
  managerId?: string | null;
  manager?: { id: string; name: string } | null;
  subManagers?: Array<{ id: string; name: string }>;
  assignedOperators: string[];
}

const FTO_LIST = [
  'Venus Travel',
  'Viva Holidays',
  'Inspiring Vacations',
  'Sanskriti Tours',
  'Soulful Safaris',
  'Direct Client',
  'Referral Client',
  '24x7 Rooms com',
  'Trip Tailor',
  'Muziris Heritage',
  'Explore',
  'Fest Travel',
  'Japan',
  'ROCS',
  'Buckenham Travel',
  'Celebration India Holidays',
  'Hooting Owls',
  'Royal Mountain Tours',
  'Flywell Tours',
  'Destinations World wide',
  'Business Promotion / FAM'
];

export function SettingsPage({ currentUser }: SettingsPageProps) {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  //Fetch all users (admin only)
  useEffect(() => {
    if (currentUser.isAdmin) {
      fetchUsers();
    }
  }, [currentUser.isAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: UserData) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          managerId: editingUser.managerId,
          assignedOperators: editingUser.assignedOperators
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchUsers();
        setShowEditModal(false);
        setEditingUser(null);
        alert('User updated successfully');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleToggleOperator = (operator: string) => {
    if (!editingUser) return;
    const current = editingUser.assignedOperators || [];
    const updated = current.includes(operator)
      ? current.filter(op => op !== operator)
      : [...current, operator];
    setEditingUser({ ...editingUser, assignedOperators: updated });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account information and system settings</p>
      </div>

      {/* Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-purple-600" />
          <h2 className="font-semibold">Profile Information</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">Name</label>
            <p className="font-medium">{currentUser.name}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600">Email</label>
            <p className="font-medium">{currentUser.email || 'Not available'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600">Role</label>
            <p className="font-medium capitalize">{currentUser.role}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600">Access Level</label>
            <p className="font-medium">{currentUser.isAdmin ? 'Full Access (Admin)' : 'Limited Access (Manager)'}</p>
          </div>
        </div>
      </motion.div>

      {/* Admin: User Management Section */}
      {currentUser.isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-purple-600" />
              <h2 className="font-semibold">User Management</h2>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Manager</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">FTOs Assigned</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map(user => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{user.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            user.isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {user.isAdmin ? 'Admin' : 'Manager'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {user.manager?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-gray-600">
                            {user.assignedOperators && user.assignedOperators.length > 0 
                              ? `${user.assignedOperators.length} FTO(s)` 
                              : 'None'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={14} />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Edit User: {editingUser.name}</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Manager Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Manager (for Sub-Manager)
                  </label>
                  <select
                    value={editingUser.managerId || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, managerId: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">No Manager (Top-Level Manager)</option>
                    {users.filter(u => u.id !== editingUser.id && !u.isAdmin).map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Assign this user under another manager to make them a sub-manager
                  </p>
                </div>

                {/* FTO Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Assign Foreign Tour Operators
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {FTO_LIST.map(operator => (
                      <label key={operator} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={editingUser.assignedOperators?.includes(operator) || false}
                          onChange={() => handleToggleOperator(operator)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm">{operator}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    User will only see tour files from selected FTOs
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveUser}
                    className="px-4 py-2 text-white bg-purple-600 rounded-md hover:bg-purple-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
